/**
 * Berechtigungs-Sets — Spiegel von backend/app/core/security.py (PERMISSION_SETS).
 *
 * Regel (Coding-Standards): Rollen im Frontend nur zum Ein-/Ausblenden —
 * durchgesetzt wird immer serverseitig.
 */

import { useAuthStore } from "@/store/auth"

export const PERMISSION_SETS = {
  /** Systemadministration */
  admin: ["admin"],
  /** Mitarbeiter/Stellen/Teams/Schichtplan anlegen + aendern */
  personal_schreiben: ["admin", "personal:leitung", "personal:pflege"],
  /** Anwesenheitsboard nutzen (ein-/ausbuchen, Abwesenheit melden) */
  board_nutzen: [
    "admin",
    "personal:leitung",
    "personal:pflege",
    "personal:basis",
    "technik:leitung",
    "technik:pflege",
    "produktion:leitung",
    "produktion:pflege",
    "labor:leitung",
    "logistik:leitung",
  ],
  /** Betriebskalender pflegen (Tagtypen, Betriebsurlaub) */
  kalender_pflegen: ["admin", "beauftragung:pflege"],
  /** Leitungs-Lesezugriff (Auswertungen, Schulungs-Controlling, Reports) */
  leitung_lesen: [
    "admin",
    "personal:leitung",
    "personal:pflege",
    "personal:basis",
    "technik:leitung",
    "technik:pflege",
    "produktion:leitung",
    "produktion:pflege",
    "labor:leitung",
    "logistik:leitung",
    "einkauf:leitung",
    "vertrieb:leitung",
    "geschaeftsfuehrung",
    "betriebsleitung",
    "extern:fachsicherheit",
  ],
  /** Dokumente/Arbeitsanweisungen schreiben (alle Leitungsrollen + admin) */
  dokumente_schreiben: [
    "admin",
    "personal:leitung",
    "personal:pflege",
    "technik:leitung",
    "produktion:leitung",
    "labor:leitung",
    "logistik:leitung",
    "einkauf:leitung",
    "vertrieb:leitung",
    "geschaeftsfuehrung",
    "betriebsleitung",
  ],
  /** Schulungen schreiben (Ninox FC.writeRoles: nur personal:pflege + admin) */
  schulung_schreiben: ["admin", "personal:pflege"],
  /** Vorsorge lesen (require_roles in examinations.py; admin besteht immer) */
  vorsorge_lesen: ["admin", "personal:leitung", "personal:pflege", "extern:fachsicherheit"],
  /** Hartes Loeschen */
  loeschen: ["admin", "loeschbuddy"],
} as const

export type PermissionSet = keyof typeof PERMISSION_SETS

export function hasPermission(
  roles: readonly string[] | undefined | null,
  set: PermissionSet
): boolean {
  if (!roles) return false
  return PERMISSION_SETS[set].some((r) => roles.includes(r))
}

/** Hook: darf der angemeldete Benutzer das Berechtigungs-Set nutzen? */
export function useCan(set: PermissionSet): boolean {
  const roles = useAuthStore((s) => s.user?.roles)
  return hasPermission(roles, set)
}
